import { BODE_FREQUENCY_SCALE, type TBodeFrequencyScale } from "../../../multi-analysis/types";

export interface IBodeArrowPoint {
  x: number;
  y: number;
}

export interface IBodeArrowSegment {
  from: number;
  to: number;
}

export interface IBodeDirectionArrow {
  x: number;
  y: number;
  angle: number;
}

export const getBodeFrequencyFactor = (scale: TBodeFrequencyScale): number =>
  scale === BODE_FREQUENCY_SCALE.HERTZ ? 1 / 60 : 1;

export const convertBodeFrequency = (rpm: number, scale: TBodeFrequencyScale): number =>
  rpm * getBodeFrequencyFactor(scale);

export const getBodeFrequencyAxisBounds = (
  frequenciesRpm: readonly number[],
  scale: TBodeFrequencyScale,
  fromZero: boolean,
): { min: number; max: number } => {
  const frequencies = frequenciesRpm.map(value => convertBodeFrequency(value, scale)).filter(Number.isFinite);
  const minimum = frequencies.length ? Math.min(...frequencies) : 0;
  const maximum = frequencies.length ? Math.max(...frequencies) : 1;
  const padding = minimum === maximum ? Math.max(1, Math.abs(minimum) * 0.01) : 0;

  return {
    min: fromZero ? 0 : minimum - padding,
    max: fromZero && maximum === 0 ? 1 : maximum + (fromZero ? 0 : padding),
  };
};

interface IBodeCursorSample {
  key: string;
  date: string;
  rotationFrequencyRpm: number | null;
}

interface IBodeCursorSeries<TSample extends IBodeCursorSample> {
  samples: readonly TSample[];
}

export const getBodeCursorOrder = <TSample extends IBodeCursorSample>(
  series: readonly IBodeCursorSeries<TSample>[],
  cursorKey: string | null,
): TSample[] => {
  const activeSeries = series.find(item => item.samples.some(sample => sample.key === cursorKey)) ?? series[0];

  return (activeSeries?.samples ?? [])
    .filter(
      (sample): sample is TSample & { rotationFrequencyRpm: number } =>
        sample.rotationFrequencyRpm !== null && Number.isFinite(sample.rotationFrequencyRpm),
    )
    .slice()
    .sort(
      (left, right) =>
        left.rotationFrequencyRpm - right.rotationFrequencyRpm ||
        left.date.localeCompare(right.date) ||
        left.key.localeCompare(right.key),
    );
};

export const getBodeArrowSegments = (points: readonly IBodeArrowPoint[]): IBodeArrowSegment[] => {
  const segments = points.flatMap((point, index) => {
    const previous = points[index - 1];
    return previous &&
      Number.isFinite(previous.x) &&
      Number.isFinite(previous.y) &&
      Number.isFinite(point.x) &&
      Number.isFinite(point.y) &&
      (previous.x !== point.x || previous.y !== point.y)
      ? [{ from: index - 1, to: index }]
      : [];
  });
  const maximumArrowCount = 7;
  if (segments.length <= maximumArrowCount) return segments;
  const selectedIndexes = new Set(
    Array.from({ length: maximumArrowCount }, (_, index) =>
      Math.round((index * (segments.length - 1)) / (maximumArrowCount - 1)),
    ),
  );
  return segments.filter((_, index) => selectedIndexes.has(index));
};

export const getBodeSegmentArrow = (from: IBodeArrowPoint, to: IBodeArrowPoint): IBodeDirectionArrow | null => {
  const length = Math.hypot(to.x - from.x, to.y - from.y);
  if (!Number.isFinite(length) || length < 2) return null;
  return {
    x: from.x + (to.x - from.x) * 0.6,
    y: from.y + (to.y - from.y) * 0.6,
    angle: Math.atan2(to.y - from.y, to.x - from.x),
  };
};
